import { useEffect, useRef } from "react";

// Tiempo máximo que la app puede estar en segundo plano antes de
// considerarse "vieja" y forzar una recarga al volver. Ajustable.
const MAX_IDLE_MS = 15 * 60 * 1000; // 15 minutos

/**
 * Cuando la app se instala como PWA (modo standalone), el navegador NO la
 * descarta/recarga automáticamente al dejarla en segundo plano, a diferencia
 * de una pestaña normal de Chrome. Esto hace que, si el usuario la deja
 * abierta un buen rato (o de un día para otro), siga mostrando datos viejos.
 *
 * Este hook detecta cuándo la app vuelve a primer plano (Page Visibility
 * API) y fuerza un window.location.reload() si estuvo oculta más de
 * `maxIdleMs` o si cambió el día mientras estaba oculta. HashRouter se
 * encarga de que, tras el reload, quede en la misma vista/URL en la que
 * estaba el usuario.
 */
export function useReloadOnResume(maxIdleMs = MAX_IDLE_MS) {
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }

      if (document.visibilityState === "visible" && hiddenAtRef.current) {
        const hiddenFor = Date.now() - hiddenAtRef.current;
        const cambioDeDia =
          new Date(hiddenAtRef.current).toDateString() !==
          new Date().toDateString();

        if (hiddenFor > maxIdleMs || cambioDeDia) {
          window.location.reload();
        }

        hiddenAtRef.current = null;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [maxIdleMs]);
}
